import React, {useEffect, useRef, useState} from 'react';
import {Box, IconButton, TextField, Tooltip, Typography} from '@mui/material';
import {useFormikContext} from 'formik';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {UploadRounded} from '@mui-symbols-material/w200';
import {DeleteRounded, EditRounded} from '@mui-symbols-material/w300';
import {isImageFile} from '../../utils/fileType';
import {NODE_IMAGE_FILE_FIELD, NODE_IMAGE_INPUT_TYPE} from '../../utils/nodeImage';

/**
 * Re-tags the backend's `inputType: "image"` attribute (index `imageId`) so it
 * reaches this renderer instead of the shared TDAForm's built-in image branch,
 * which runs before `customInputRenderers` and cannot preview the current
 * image, clear it or restrict the picker to images. `previewUrl` is the
 * thumbnail of the node's current image (edit forms); frontend-only field,
 * like `_includeTrash` on the picker attributes.
 */
export const withNodeImageInputType = (attributes: any[], previewUrl?: string | null): any[] =>
    (attributes ?? []).map((attr) =>
        attr?.inputType === 'image'
            ? {...attr, inputType: NODE_IMAGE_INPUT_TYPE, _previewUrl: previewUrl ?? null}
            : attr
    );

/** Props injected by TDAForm's `customInputRenderers` slot. */
interface Props {
    attribute: any;
    /** Current `imageId` form value: file-service UUID of the saved image, or ''/null. */
    value: any;
    error?: string;
    touched?: boolean;
    setFieldValue: (field: string, value: any) => void;
}

/** Custom data handed to the drop area through TextField's `inputProps`. */
interface DropAreaOwnProps {
    disabled: boolean;
    hasImage: boolean;
    previewUrl: string | null;
    pickedFileName?: string;
    onOpenPicker: () => void;
    onRemove: (event: React.MouseEvent) => void;
    onDropFile: (file: File | undefined | null) => void;
}

/**
 * The "input" slot of the outlined TextField: a focusable drop area instead of
 * an <input>. Receives MUI's slot props (className, focus handlers, aria) plus
 * our own via `inputProps`; only the DOM-relevant ones are forwarded.
 */
const NodeImageDropArea = React.forwardRef<HTMLDivElement, any>(function NodeImageDropArea(props, ref) {
    const {
        className,
        id,
        onFocus,
        onBlur,
        'aria-describedby': ariaDescribedBy,
        disabled,
        hasImage,
        previewUrl,
        pickedFileName,
        onOpenPicker,
        onRemove,
        onDropFile,
    } = props as DropAreaOwnProps & Record<string, any>;
    const {t} = useTranslation();

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpenPicker();
        }
    };

    return (
        <div
            ref={ref}
            id={id}
            className={className}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            aria-describedby={ariaDescribedBy}
            onFocus={onFocus}
            onBlur={onBlur}
            onClick={() => { if (!disabled) onOpenPicker(); }}
            onKeyDown={handleKeyDown}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
                event.preventDefault();
                if (!disabled) onDropFile(event.dataTransfer?.files?.[0]);
            }}
        >
            {hasImage ? (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2, width: '100%', minWidth: 0}}>
                    {previewUrl && (
                        <img
                            src={previewUrl}
                            alt=""
                            data-testid="node-image-preview"
                            style={{maxHeight: 100, maxWidth: '50%', objectFit: 'contain', borderRadius: 4, display: 'block'}}
                        />
                    )}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{flex: 1, minWidth: 0}}
                    >
                        {pickedFileName ?? ''}
                    </Typography>
                    {!disabled && (
                        <Box sx={{display: 'flex', gap: 0.5, flexShrink: 0}}>
                            <Tooltip title={t('Replace image')}>
                                <IconButton
                                    size="small"
                                    aria-label={t('Replace image')}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onOpenPicker();
                                    }}
                                >
                                    <EditRounded fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('Remove image')}>
                                <IconButton
                                    size="small"
                                    aria-label={t('Remove image')}
                                    data-testid="node-image-remove"
                                    onClick={onRemove}
                                >
                                    <DeleteRounded fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            ) : (
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1}}>
                    <Typography variant="body1" color="text.secondary">
                        {t('Click or drop an image here')}
                    </Typography>
                    <UploadRounded sx={{color: 'primary.main'}}/>
                </Box>
            )}
        </div>
    );
});

/**
 * Single-image input for the node forms, rendered as a regular outlined
 * TextField (same border, notch, hover and focus as every other input) whose
 * input slot is a drop area. The picked `File` lives under the Formik-only
 * `_imageFile` key — `imageId` itself stays a string so the generated Yup
 * schema keeps validating. Clearing writes `imageId: ''`, which the submit
 * helper turns into `null` (unlink).
 */
const NodeImageInput = ({attribute, value, error, touched, setFieldValue}: Props) => {
    const {t} = useTranslation();
    const {values} = useFormikContext<Record<string, any>>();
    const inputRef = useRef<HTMLInputElement>(null);

    const rawPicked = values?.[NODE_IMAGE_FILE_FIELD];
    const pickedFile: File | undefined = typeof File !== 'undefined' && rawPicked instanceof File ? rawPicked : undefined;
    const disabled = !!attribute.disabled;
    const required = !!attribute?.rules?.some((rule: any) => rule?.name === 'required');

    // Object URL for the freshly picked file (revoked on change/unmount).
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!pickedFile) {
            setObjectUrl(null);
            return;
        }
        const url = URL.createObjectURL(pickedFile);
        setObjectUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [pickedFile]);

    const hasSavedImage = typeof value === 'string' && value !== '';
    const previewUrl = objectUrl ?? (hasSavedImage ? attribute._previewUrl ?? null : null);
    const hasImage = !!pickedFile || hasSavedImage;

    const acceptFile = (file: File | undefined | null) => {
        if (!file) return;
        if (!isImageFile(file.type)) {
            toast.error(t('Only image files are allowed.'));
            return;
        }
        setFieldValue(NODE_IMAGE_FILE_FIELD, file);
    };

    const openPicker = () => {
        if (!disabled) inputRef.current?.click();
    };

    const handleRemove = (event: React.MouseEvent) => {
        event.stopPropagation();
        setFieldValue(attribute.index, '');
        setFieldValue(NODE_IMAGE_FILE_FIELD, undefined);
    };

    const dropAreaProps: DropAreaOwnProps = {
        disabled,
        hasImage,
        previewUrl,
        pickedFileName: pickedFile?.name,
        onOpenPicker: openPicker,
        onRemove: handleRemove,
        onDropFile: acceptFile,
    };

    return (
        <Box sx={{width: '100%'}} data-testid="node-image-input">
            <TextField
                fullWidth
                size="small"
                variant="outlined"
                id={attribute.index}
                name={attribute.index}
                label={`${attribute.name}${required ? ' *' : ''}`}
                disabled={disabled}
                error={Boolean(touched && error)}
                helperText={touched ? error : undefined}
                slotProps={{
                    inputLabel: {shrink: true},
                    input: {
                        inputComponent: NodeImageDropArea as any,
                        inputProps: dropAreaProps,
                    },
                }}
                sx={{
                    '& .MuiInputBase-root': {
                        padding: 0,
                        alignItems: 'stretch',
                        cursor: disabled ? 'default' : 'pointer',
                    },
                    // The slot is a div, not an <input>: undo the text-field sizing.
                    '& .MuiInputBase-input': {
                        height: 'auto',
                        minHeight: 130,
                        padding: '12px 14px',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'inherit',
                        outline: 'none',
                    },
                }}
            />
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                hidden
                data-testid="node-image-file-input"
                onChange={(event) => {
                    acceptFile(event.target.files?.[0]);
                    // allow re-picking the same file
                    event.target.value = '';
                }}
            />
        </Box>
    );
};

export default NodeImageInput;
